import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hash, Loader2, Sparkles, Heart, Calendar, User, Gem } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";

const NUMBER_COLORS = {
  1: "from-red-500/20 to-orange-500/20 border-red-500/30",
  2: "from-blue-500/20 to-cyan-500/20 border-blue-500/30",
  3: "from-yellow-500/20 to-amber-500/20 border-yellow-500/30",
  4: "from-green-500/20 to-emerald-500/20 border-green-500/30",
  5: "from-sky-500/20 to-blue-500/20 border-sky-500/30",
  6: "from-pink-500/20 to-rose-500/20 border-pink-500/30",
  7: "from-purple-500/20 to-violet-500/20 border-purple-500/30",
  8: "from-amber-500/20 to-yellow-500/20 border-amber-500/30",
  9: "from-indigo-500/20 to-blue-500/20 border-indigo-500/30",
  11: "from-violet-500/20 to-purple-500/20 border-violet-500/30",
  22: "from-white/20 to-slate-500/20 border-white/30",
  33: "from-rose-500/20 to-pink-500/20 border-rose-500/30",
};

const NumberCard = ({ title, number, meaning, icon, delay = 0 }) => {
  if (!number) return null;
  const colorClass = NUMBER_COLORS[number] || NUMBER_COLORS[9];
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <Card className={`glass bg-gradient-to-br ${colorClass} border`} data-testid={`number-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-mystic-200/60 mb-1">{title}</p>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold text-white">{number}</span>
                {icon}
              </div>
              <p className="text-mystic-200/80 text-sm leading-relaxed">{meaning}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const Numerology = () => {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compatibility, setCompatibility] = useState(null);
  const [partnerDate, setPartnerDate] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [calcLoading, setCalcLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/numerology/profile`, { headers, withCredentials: true });
      setProfile(res.data);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  const calculateCompatibility = async () => {
    if (!partnerDate) {
      toast.error("Please enter partner's birth date");
      return;
    }
    setCalcLoading(true);
    try {
      const res = await axios.post(`${API}/numerology/compatibility`, {
        partner_birth_date: partnerDate,
        partner_name: partnerName,
      }, { headers, withCredentials: true });
      setCompatibility(res.data);
      toast.success("Compatibility calculated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate compatibility");
    } finally {
      setCalcLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="numerology-loading">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  if (!profile?.has_profile) {
    return (
      <div className="space-y-6 p-4 md:p-6" data-testid="numerology-no-profile">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            <Hash className="w-8 h-8 inline mr-2 text-mystic-300" />
            Sacred Numerology
          </h1>
          <p className="text-mystic-200/70">Discover the sacred numbers woven into your life path</p>
        </motion.div>
        <Card className="glass">
          <CardContent className="p-8 text-center">
            <Hash className="w-16 h-16 text-mystic-300/50 mx-auto mb-4" />
            <h2 className="text-xl text-white mb-2">Birth Chart Required</h2>
            <p className="text-mystic-200/70 mb-4">Save your birth date in the Astrology section to unlock your numerology profile.</p>
            <Button className="bg-mystic-600 hover:bg-mystic-500 text-white rounded-full" onClick={() => window.location.href = "/astrology"} data-testid="go-to-astrology-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Go to Astrology
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const lp = profile.life_path;

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="numerology-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          <Hash className="w-8 h-8 inline mr-2 text-mystic-300" />
          Sacred Numerology
        </h1>
        <p className="text-mystic-200/70">The hidden numbers that shape your destiny</p>
      </motion.div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="glass w-full mb-6">
          <TabsTrigger value="profile" className="flex-1" data-testid="tab-profile">My Numbers</TabsTrigger>
          <TabsTrigger value="compatibility" className="flex-1" data-testid="tab-compatibility">Compatibility</TabsTrigger>
        </TabsList>

        {/* PROFILE TAB */}
        <TabsContent value="profile" className="space-y-6">
          {/* Life Path - Hero Card */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className={`glass bg-gradient-to-br ${NUMBER_COLORS[lp.number] || NUMBER_COLORS[9]} border overflow-hidden`} data-testid="life-path-card">
              <CardContent className="p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="text-center md:text-left">
                    <p className="text-xs uppercase tracking-[0.2em] text-mystic-200/60 mb-2">Life Path Number</p>
                    <div className="text-7xl md:text-8xl font-bold text-white mb-2" data-testid="life-path-number">{lp.number}</div>
                    <h2 className="text-2xl text-white font-semibold mb-1" data-testid="life-path-name">{lp.name}</h2>
                  </div>
                  <div className="flex-1 space-y-3">
                    <p className="text-mystic-200/80 leading-relaxed">{lp.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {lp.traits?.map((trait) => (
                        <Badge key={trait} className="bg-white/10 text-white border-white/20">{trait}</Badge>
                      ))}
                    </div>
                    <div className="glass-light rounded-lg p-3 mt-3">
                      <p className="text-xs uppercase tracking-wider text-moon-gold/70 mb-1">Cycle Connection</p>
                      <p className="text-mystic-200/80 text-sm">{lp.cycle_connection}</p>
                    </div>
                    <div className="flex gap-4 mt-2">
                      <span className="text-sm text-mystic-200/60">
                        <Gem className="w-3.5 h-3.5 inline mr-1 text-purple-300" /> {lp.crystal}
                      </span>
                      <span className="text-sm text-mystic-200/60">
                        <span className="w-3 h-3 rounded-full inline-block mr-1" style={{ backgroundColor: lp.color?.toLowerCase() }}></span> {lp.color}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Number Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberCard
              title="Expression Number"
              number={profile.expression?.number}
              meaning={profile.expression?.meaning}
              icon={<Sparkles className="w-5 h-5 text-amber-300/60" />}
              delay={0.1}
            />
            <NumberCard
              title="Soul Urge Number"
              number={profile.soul_urge?.number}
              meaning={profile.soul_urge?.meaning}
              icon={<Heart className="w-5 h-5 text-rose-300/60" />}
              delay={0.15}
            />
            <NumberCard
              title="Personality Number"
              number={profile.personality?.number}
              meaning={profile.personality?.meaning}
              icon={<User className="w-5 h-5 text-blue-300/60" />}
              delay={0.2}
            />
            <NumberCard
              title="Birthday Number"
              number={profile.birthday?.number}
              meaning={profile.birthday?.meaning}
              icon={<Calendar className="w-5 h-5 text-green-300/60" />}
              delay={0.25}
            />
          </div>

          {/* Personal Year */}
          {profile.personal_year && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card className="glass border-moon-gold/20" data-testid="personal-year-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-moon-gold" />
                    Personal Year {profile.personal_year.year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <span className="text-5xl font-bold text-moon-gold">{profile.personal_year.number}</span>
                    <p className="text-mystic-200/80 text-sm leading-relaxed">{profile.personal_year.theme}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </TabsContent>

        {/* COMPATIBILITY TAB */}
        <TabsContent value="compatibility" className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="glass" data-testid="compatibility-form">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-rose-400" />
                  Numerology Compatibility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-mystic-200/70 text-sm">Discover the numerological connection between you and your partner.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-mystic-200">Partner's Name</Label>
                    <Input
                      type="text"
                      value={partnerName}
                      onChange={(e) => setPartnerName(e.target.value)}
                      placeholder="Partner's name"
                      className="bg-mystic-800/50 border-mystic-600 text-white"
                      data-testid="partner-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-mystic-200">Partner's Birth Date</Label>
                    <Input
                      type="date"
                      value={partnerDate}
                      onChange={(e) => setPartnerDate(e.target.value)}
                      className="bg-mystic-800/50 border-mystic-600 text-white"
                      data-testid="partner-date-input"
                    />
                  </div>
                </div>
                <Button onClick={calculateCompatibility} disabled={calcLoading} className="bg-rose-600 hover:bg-rose-500 text-white rounded-full" data-testid="calc-compatibility-btn">
                  {calcLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
                  Calculate Compatibility
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Compatibility Result */}
          {compatibility && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <Card className="glass border-rose-500/20" data-testid="compatibility-result">
                <CardContent className="p-6 md:p-8">
                  {/* Score */}
                  <div className="text-center mb-6">
                    <div className="text-6xl font-bold text-rose-400 mb-1" data-testid="compatibility-score">{compatibility.compatibility_score}%</div>
                    <Badge className={`text-sm ${
                      compatibility.harmony === 'mirror' ? 'bg-purple-500/20 text-purple-300' :
                      compatibility.harmony === 'harmonious' ? 'bg-green-500/20 text-green-300' :
                      compatibility.harmony === 'growth' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-blue-500/20 text-blue-300'
                    }`} data-testid="harmony-badge">
                      {compatibility.harmony === 'mirror' ? 'Mirror Connection' :
                       compatibility.harmony === 'harmonious' ? 'Harmonious' :
                       compatibility.harmony === 'growth' ? 'Growth-Oriented' : 'Balanced'}
                    </Badge>
                  </div>

                  <p className="text-mystic-200/80 text-center mb-6">{compatibility.description}</p>

                  {/* Side by Side */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="glass-light rounded-xl p-4 text-center">
                      <p className="text-xs text-mystic-200/50 uppercase tracking-wider mb-1">You</p>
                      <div className="text-3xl font-bold text-white mb-1">{compatibility.user_life_path.number}</div>
                      <p className="text-mystic-300 text-sm">{compatibility.user_life_path.name}</p>
                    </div>
                    <div className="glass-light rounded-xl p-4 text-center">
                      <p className="text-xs text-mystic-200/50 uppercase tracking-wider mb-1">{compatibility.partner_name || "Partner"}</p>
                      <div className="text-3xl font-bold text-white mb-1">{compatibility.partner_life_path.number}</div>
                      <p className="text-mystic-300 text-sm">{compatibility.partner_life_path.name}</p>
                    </div>
                  </div>

                  {/* Combined Number */}
                  <div className="glass-light rounded-xl p-4 text-center">
                    <p className="text-xs text-mystic-200/50 uppercase tracking-wider mb-1">Combined Energy</p>
                    <div className="text-2xl font-bold text-moon-gold mb-1">{compatibility.combined_number.number}</div>
                    <p className="text-mystic-200/70 text-sm">{compatibility.combined_number.meaning}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Numerology;
